import { ReactNode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertDialog, Button, Flex } from '@radix-ui/themes';
import { Cross2Icon } from '@radix-ui/react-icons';

type AlertType = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  message: string;
  type: AlertType;
  onClose: () => void;
}

const AlertComponent = ({ message, type, onClose }: AlertProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = {
    success: 'var(--green-9)',
    error: 'var(--red-9)',
    warning: 'var(--yellow-9)',
    info: 'var(--blue-9)'
  }[type];

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: 50,
        backgroundColor: bgColor,
        color: 'white',
        padding: '0.75rem 1rem',
        borderRadius: 'var(--radius-3)',
        transition: 'all 300ms',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(-0.5rem)'
      }}
    >
      <Flex gap="3" align="center">
        <span>{message}</span>
        <Button variant="ghost" onClick={() => setIsVisible(false)} style={{ padding: '0.25rem' }}>
          <Cross2Icon />
        </Button>
      </Flex>
    </div>
  );
};

export const showAlert = (message: string, type: AlertType = 'info'): void => {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(
    <AlertComponent
      message={message}
      type={type}
      onClose={() => {
        root.unmount();
        document.body.removeChild(container);
      }}
    />
  );
};

export const showConfirm = (message: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const handleClose = (result: boolean) => {
      root.unmount();
      document.body.removeChild(container);
      resolve(result);
    };

    const root = createRoot(container);
    root.render(
      <AlertDialog.Root>
        <AlertDialog.Content>
          <AlertDialog.Title>Conferma</AlertDialog.Title>
          <AlertDialog.Description>
            {message}
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray" onClick={() => handleClose(false)}>
                Annulla
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button onClick={() => handleClose(true)}>
                Conferma
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    );
  });
};